import { Box, Button, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import styles from '../Person.module.scss';
import { memo, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BaseRow from '~/components/common/base-row/BaseRow';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import {
  ArrowRight3Icon,
  BloodPressureIcon,
  FallIcon,
  FootstepFilledIcon,
  HeartIcon,
  Spo2Icon,
  TemperatureAddIcon,
  WalkingIcon,
} from '~/assets/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import personApi from '~/apis/person';
import { cast, handleApiError } from '~/utils/helper';
import { IPersonSummaryHealthStats } from '~/utils/interface/person';
import dayjs from 'dayjs';
import BaseDatePicker from '~/components/common/base-date-picker/BaseDatePicker';

const cx = classNames.bind(styles);

const HEALTH_STAT_TYPE_ALL = 'all';
const HEALTH_CHART_ABOVE_SLOT_CLAMP = 'clamp(min(396px, 50vh), 37vmin, 560px)';
const HEALTH_SURFACE_RADIUS_PX = 12;
const CHART_MARGIN_LAST_TICK_RIGHT_EXTRA_PX = 40;
const CHART_MARGIN_RIGHT_PX = 16 + CHART_MARGIN_LAST_TICK_RIGHT_EXTRA_PX;
const CHART_MARGIN_LEFT_PX = 8;
const CHART_LEGEND_COLUMN_WIDTH_PX = 200;

const HEALTH_CHART_SERIES = {
  heart: { color: '#ff2d6f' },
  spo2: { color: '#7C8796' },
  temp: { color: '#1e3a8a' },
  bp: { color: '#ef4444' },
  steps: { color: '#3b82f6' },
  idle: { color: '#f97316' },
  fall: { color: '#D6A600' },
} as const;

const CHART_PX_PER_DATA_POINT = 110;
const CHART_INNER_PAD_PX = 340;
const CHART_INNER_MIN_PX = 680;
const CHART_LINE_STROKE_WIDTH = 2;

const Y_AXIS_REFERENCE_TICKS = [0, 20, 40, 60, 80, 100, 120, 140, 160, 180, 200];

const getNiceUpperMax = (max: number): number => {
  if (max <= 100) return 200;
  const range = max - 100;
  const power = Math.pow(10, Math.floor(Math.log10(range)));
  const fraction = range / power;
  let niceFraction;
  if (fraction <= 1) niceFraction = 1;
  else if (fraction <= 2) niceFraction = 2;
  else if (fraction <= 5) niceFraction = 5;
  else niceFraction = 10;
  const niceRange = niceFraction * power;
  return 100 + niceRange;
};

const transformDataValue = (value: number | null, niceMax: number): number | null => {
  if (value === null || value === undefined) return null;
  if (value <= 100) return value;
  const range = Math.max(niceMax - 100, 1);
  const normalized = (value - 100) / range;
  const transformed = 100 + normalized * 100;
  return transformed;
};

const formatHealthChartTime = (timestamp?: string) => (timestamp ? dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss') : '');

type HealthChartLegendItem = { color: string; value: string };

const HealthChartLegend = memo(function HealthChartLegend({
  items,
  zoneHeightPx,
}: {
  items: HealthChartLegendItem[];
  zoneHeightPx: number | string;
}) {
  return (
    <Box
      sx={{
        height: zoneHeightPx,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 1.5,
        pl: 0.5,
        pr: 0.5,
      }}
    >
      {items.map((entry, index) => (
        <Box key={`${entry.value}-${index}`} sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: 32, height: 6, flexShrink: 0, bgcolor: entry.color, mr: 1 }} />
          <Typography component='span'>{entry.value}</Typography>
        </Box>
      ))}
    </Box>
  );
});

const HealthChartTooltip = memo(function HealthChartTooltip({ active, payload }: any) {
  const { t } = useTranslation('admin/person');
  if (!active || !payload || payload.length === 0) return null;
  const data = payload[0].payload;
  const originalValues = data.original || {};
  const tooltipItems = [
    { label: t('detail.heart_rate'), value: originalValues.heart, color: HEALTH_CHART_SERIES.heart.color },
    { label: t('detail.sp02'), value: originalValues.spo2, color: HEALTH_CHART_SERIES.spo2.color },
    { label: t('detail.body_temperature'), value: originalValues.temp, color: HEALTH_CHART_SERIES.temp.color },
    { label: t('detail.blood_pressure'), value: originalValues.bp, color: HEALTH_CHART_SERIES.bp.color },
    { label: t('detail.step_count'), value: originalValues.steps, color: HEALTH_CHART_SERIES.steps.color },
    // Ẩn UI: Phát hiện không chuyển động
    // { label: t('detail.no_movement_detected'), value: originalValues.idle, color: HEALTH_CHART_SERIES.idle.color },
    // Ẩn UI: Cảnh báo ngã
    // { label: t('detail.fall_detected'), value: originalValues.fall, color: HEALTH_CHART_SERIES.fall.color },
  ];
  return (
    <Box
      sx={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        padding: '12px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
      }}
    >
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#161A1F', mb: 1 }}>{data.time}</Typography>
      {tooltipItems.map((item, index) => (
        <Box
          key={`${item.label}-${index}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: '12px',
            mb: index < tooltipItems.length - 1 ? 0.5 : 0,
          }}
        >
          <Typography sx={{ fontSize: '12px', color: item.color, fontWeight: 500 }}>{item.label}:</Typography>
          <Typography sx={{ fontSize: '12px', fontWeight: 500, color: item.color, marginLeft: 'auto' }}>
            {item.value !== null && item.value !== undefined ? item.value : '—'}
          </Typography>
        </Box>
      ))}
    </Box>
  );
});

const PersonHealth = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('admin/person');

  const [healthStats, setHealthStats] = useState<Array<any>>([]);
  const [personSummaryHealthStats, setPersonSummaryHealthStats] = useState<IPersonSummaryHealthStats>();
  const [selectedDate, setSelectedDate] = useState<Date>(() => dayjs().startOf('day').toDate());
  const [healthType, setHealthType] = useState<string>(HEALTH_STAT_TYPE_ALL);

  const chartLayout = useMemo(() => {
    return {
      margin: { top: 5, right: CHART_MARGIN_RIGHT_PX, bottom: 0, left: CHART_MARGIN_LEFT_PX },
      legendZoneHeightPx: '100%',
      xAxisHeightPx: 30,
      xTickMarginPx: 12,
    };
  }, []);

  const healthTypeOptions = useMemo(
    () => [
      { id: HEALTH_STAT_TYPE_ALL, name: t('detail.health_type_all') },
      { id: 'heart_rate', name: t('detail.heart_rate') },
      { id: 'spo2', name: t('detail.sp02') },
      { id: 'body_temperature', name: t('detail.body_temperature') },
      { id: 'blood_pressure', name: t('detail.blood_pressure') },
      { id: 'step_count', name: t('detail.step_count') },
      // Ẩn UI: Phát hiện không chuyển động
      // { id: 'no_movement_detected', name: t('detail.no_movement_detected') },
      // Ẩn UI: Cảnh báo ngã
      // { id: 'fall_detected', name: t('detail.fall_detected') },
    ],
    [t]
  );

  const rawDataMax = useMemo(() => {
    let max = 100;
    healthStats.forEach((row) => {
      const values = [
        row.heart_rate,
        row.spo2,
        row.body_temperature,
        row.blood_pressure?.systolic,
        row.step_count,
        row.no_movement_detected,
        row.no_movement_count,
        row.fall_detected,
        row.fall_count,
        row.value,
      ].filter((v) => v !== null && v !== undefined) as number[];

      if (values.length === 0 && healthType !== HEALTH_STAT_TYPE_ALL) {
        values.push(1);
      }

      if (values.length > 0) {
        const rowMax = Math.max(...values);
        if (rowMax > max) max = rowMax;
      }
    });
    return max;
  }, [healthStats, healthType]);

  const niceDataMax = useMemo(() => getNiceUpperMax(rawDataMax), [rawDataMax]);

  const chartData = useMemo(
    () =>
      healthStats.map((row) => {
        const getValue = (primaryKey: string) => {
          if (row[primaryKey] !== undefined && row[primaryKey] !== null) return row[primaryKey];
          if (
            healthType === primaryKey ||
            (primaryKey === 'idle' && healthType === 'no_movement_detected') ||
            (primaryKey === 'fall' && healthType === 'fall_detected')
          ) {
            if (row.value !== undefined && row.value !== null) return row.value;
            if (primaryKey === 'idle' && row.no_movement_count !== undefined) return row.no_movement_count;
            if (primaryKey === 'fall' && row.fall_count !== undefined) return row.fall_count;
            return 1;
          }
          return null;
        };

        const heart = getValue('heart_rate');
        const spo2 = getValue('spo2');
        const temp = getValue('body_temperature');
        const bp = row.blood_pressure?.systolic ?? (healthType === 'blood_pressure' ? row.value : null);
        const steps = getValue('step_count');
        const idle = getValue('no_movement_detected') ?? getValue('idle');
        const fall = getValue('fall_detected') ?? getValue('fall');

        return {
          time: formatHealthChartTime(row.timestamp),
          heart: transformDataValue(heart, niceDataMax),
          spo2: transformDataValue(spo2, niceDataMax),
          temp: transformDataValue(temp, niceDataMax),
          bp: transformDataValue(bp, niceDataMax),
          steps: transformDataValue(steps, niceDataMax),
          idle: transformDataValue(idle, niceDataMax),
          fall: transformDataValue(fall, niceDataMax),
          original: { heart, spo2, temp, bp, steps, idle, fall },
        };
      }),
    [healthStats, niceDataMax, healthType]
  );

  const chartInnerWidthPx = useMemo(() => {
    const fromData = chartData.length * CHART_PX_PER_DATA_POINT + CHART_INNER_PAD_PX;
    return Math.max(CHART_INNER_MIN_PX, fromData);
  }, [chartData.length]);

  const showChartSeries = useMemo(
    () => ({
      heart: healthType === HEALTH_STAT_TYPE_ALL || healthType === 'heart_rate',
      spo2: healthType === HEALTH_STAT_TYPE_ALL || healthType === 'spo2',
      temp: healthType === HEALTH_STAT_TYPE_ALL || healthType === 'body_temperature',
      bp: healthType === HEALTH_STAT_TYPE_ALL || healthType === 'blood_pressure',
      steps: healthType === HEALTH_STAT_TYPE_ALL || healthType === 'step_count',
      idle: healthType === HEALTH_STAT_TYPE_ALL || healthType === 'no_movement_detected',
      fall: healthType === HEALTH_STAT_TYPE_ALL || healthType === 'fall_detected',
    }),
    [healthType]
  );

  const healthChartLegendItems = useMemo((): HealthChartLegendItem[] => {
    const rows: HealthChartLegendItem[] = [];
    if (showChartSeries.heart) rows.push({ color: HEALTH_CHART_SERIES.heart.color, value: t('detail.heart_rate') });
    if (showChartSeries.spo2) rows.push({ color: HEALTH_CHART_SERIES.spo2.color, value: t('detail.sp02') });
    if (showChartSeries.temp) rows.push({ color: HEALTH_CHART_SERIES.temp.color, value: t('detail.body_temperature') });
    if (showChartSeries.bp) rows.push({ color: HEALTH_CHART_SERIES.bp.color, value: t('detail.blood_pressure') });
    if (showChartSeries.steps) rows.push({ color: HEALTH_CHART_SERIES.steps.color, value: t('detail.step_count') });
    // Ẩn UI: Phát hiện không chuyển động
    // if (showChartSeries.idle)
    //   rows.push({ color: HEALTH_CHART_SERIES.idle.color, value: t('detail.no_movement_detected') });
    // Ẩn UI: Cảnh báo ngã
    // if (showChartSeries.fall) rows.push({ color: HEALTH_CHART_SERIES.fall.color, value: t('detail.fall_detected') });
    return rows;
  }, [showChartSeries, t]);

  const fetchHealthStats = async (personId: string, date: Date, type: string) => {
    try {
      const response = await personApi.getHealthStats(personId, {
        date: dayjs(date).format('YYYY/MM/DD'),
        type: type || HEALTH_STAT_TYPE_ALL,
      });
      setHealthStats(response.data.result.data ?? []);
      setPersonSummaryHealthStats(response.data.result.summary);
    } catch (e) {
      handleApiError(e);
    }
  };

  useEffect(() => {
    if (id) void fetchHealthStats(id, selectedDate, healthType);
  }, [id, selectedDate, healthType]);

  const handleCalendarDateChange = (date: string | null) => {
    setSelectedDate(date ? dayjs(date).toDate() : dayjs().startOf('day').toDate());
  };

  const handleClearAll = () => {
    setSelectedDate(dayjs().startOf('day').toDate());
    setHealthType(HEALTH_STAT_TYPE_ALL);
  };

  return (
    <Box className={cx('person-health-screen')} sx={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
      <Box className='flex shrink-0 items-center bg-[#123B6D] h-20 px-6'>
        <Typography
          className='text-white font-semibold text-xl hover:cursor-pointer'
          onClick={() => navigate(`/admin/persons/${id}`)}
        >
          {t('detail.title')}
        </Typography>
        <Box className='w-8 h-8 flex items-center justify-center'>
          <ArrowRight3Icon stroke='#fff' />
        </Box>
        <Typography className='text-white font-semibold text-xl'>{t('detail.health_document')}</Typography>
      </Box>

      <BaseRow className='shrink-0 gap-4 px-6 pt-6 mb-5 !items-end'>
        <Box className='flex-1'>
          <Typography className='text-[#161A1F] font-medium text-sm mb-2 min-w-[300px]'>{t('day')}</Typography>
          <BaseDatePicker
            portalId='datePicker-portalId'
            value={selectedDate}
            dateFormat='YYYY-MM-DD'
            onChange={(date) => handleCalendarDateChange(date)}
            placeholder={t('endDate', { ns: 'admin/common' })}
            className='w-full'
          />
        </Box>
        <Box className='flex-1'>
          <Typography className='text-[#161A1F] font-medium text-sm mb-2 min-w-[330px]'>{t('health_stats')}</Typography>
          <BaseSelect
            options={healthTypeOptions}
            value={healthType}
            placeholder={t('health_stats')}
            name='health_stat_type'
            isSearchEnable
            isDisplayAllOption={false}
            onChange={(e) => setHealthType(String(e.target.value))}
          />
        </Box>
        <Button
          className='border border-solid border-[#123B6D] bg-[#DCE8F5] rounded-xl text-base font-bold text-[#123B6D] w-[132px] hover:bg-[#0E2F58] hover:opacity-80 hover:text-white'
          onClick={handleClearAll}
        >
          {t('detail.clear_all')}
        </Button>
      </BaseRow>

      <BaseRow className='shrink-0 gap-4 px-6 pt-6 mb-5'>
        <Box className='flex-1 bg-white flex flex-col justify-center items-center text-center p-4 rounded-xl h-[175px]'>
          <HeartIcon className='w-16 h-16' />
          <Typography className='text-3xl font-bold my-1.5'>{cast(personSummaryHealthStats?.heart_rate)}</Typography>
          <Typography className='text-[#404854] text-sm'>{t('detail.heart_rate')}</Typography>
        </Box>
        <Box className='flex-1 bg-white flex flex-col justify-center items-center text-center p-4 rounded-xl h-[175px]'>
          <Spo2Icon className='w-16 h-16' fill='#7C8796' />
          <Typography className='text-3xl font-bold my-1.5'>{cast(personSummaryHealthStats?.spo2)}</Typography>
          <Typography className='text-[#404854] text-sm'>{t('detail.sp02')}</Typography>
        </Box>
        <Box className='flex-1 bg-white flex flex-col justify-center items-center text-center p-4 rounded-xl h-[175px]'>
          <TemperatureAddIcon className='w-16 h-16' fill='#123B6D' />
          <Typography className='text-3xl font-bold my-1.5'>{cast(personSummaryHealthStats?.temperature)}</Typography>
          <Typography className='text-[#404854] text-sm'>{t('detail.body_temperature')}</Typography>
        </Box>
        <Box className='flex-1 bg-white flex flex-col justify-center items-center text-center p-4 rounded-xl h-[175px]'>
          <BloodPressureIcon className='w-16 h-16' />
          <Typography className='text-3xl font-bold my-1.5'>
            {cast(personSummaryHealthStats?.blood_pressure)}
          </Typography>
          <Typography className='text-[#404854] text-sm'>{t('detail.blood_pressure')}</Typography>
        </Box>
        <Box className='flex-1 bg-white flex flex-col justify-center items-center text-center p-4 rounded-xl h-[175px]'>
          <FootstepFilledIcon className='w-16 h-16' fill='#2E89C8' />
          <Typography className='text-3xl font-bold my-1.5'>{cast(personSummaryHealthStats?.total_steps)}</Typography>
          <Typography className='text-[#404854] text-sm'>{t('detail.step_count')}</Typography>
        </Box>
        {/* Ẩn UI: Phát hiện không chuyển động
        <Box className='flex-1 bg-white flex flex-col justify-center items-center text-center p-4 rounded-xl h-[175px]'>
          <WalkingIcon className='w-16 h-16 mb-1.5' />
          <Typography className='text-3xl font-bold'>{cast(personSummaryHealthStats?.no_movement_count)}</Typography>
          <Typography className='text-[#404854] text-sm'>{t('detail.no_movement_detected')}</Typography>
        </Box>
        */}
        {/* Ẩn UI: Cảnh báo ngã
        <Box className='flex-1 bg-white flex flex-col justify-center items-center text-center p-4 rounded-xl h-[175px]'>
          <FallIcon className='w-16 h-16' />
          <Typography className='text-3xl font-bold my-1.5'>{cast(personSummaryHealthStats?.fall_count)}</Typography>
          <Typography className='text-[#404854] text-sm'>{t('detail.fall_detected')}</Typography>
        </Box>
        */}
      </BaseRow>

      <Box
        className='flex-1 flex flex-col px-6 pb-6 pt-6'
        sx={{ maxHeight: `calc(100dvh - 400px)`, minHeight: `calc(100dvh - ${HEALTH_CHART_ABOVE_SLOT_CLAMP})` }}
      >
        <Box
          sx={{
            flex: '1 1 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            pt: 2,
            px: 1.5,
            borderRadius: `${HEALTH_SURFACE_RADIUS_PX}px`,
            border: '1px solid #F3F6F9',
            bgcolor: '#FFFFFF',
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          <Typography component='p' className='m-0 mb-[0px] text-lg font-semibold text-[#161A1F]'>
            {t('detail.health_summary')}
          </Typography>
          <Box
            sx={{
              flex: '1 1 0',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'stretch',
              gap: 2,
              minHeight: 0,
              pb: 0,
            }}
          >
            <Box sx={{ flex: '1 1 auto', minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flex: '1 1 0', minHeight: 0, overflowX: 'auto', overflowY: 'hidden', pb: 0 }}>
                <Box sx={{ width: `max(100%, ${chartInnerWidthPx}px)`, height: '100%', flexShrink: 0 }}>
                  <ResponsiveContainer width='100%' height='100%'>
                    <LineChart data={chartData} margin={chartLayout.margin}>
                      <CartesianGrid strokeDasharray='3 3' vertical={false} />
                      {Y_AXIS_REFERENCE_TICKS.map((tick) => (
                        <ReferenceLine
                          key={`ref-${tick}`}
                          y={tick}
                          yAxisId='left'
                          stroke='#e0e0e0'
                          strokeDasharray='3 3'
                        />
                      ))}
                      <XAxis
                        dataKey='time'
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                        tickMargin={chartLayout.xTickMarginPx}
                        tick={{ fontSize: 10, fill: '#404854', dx: -2 }}
                        height={chartLayout.xAxisHeightPx}
                        padding={{ left: 0.02, right: 0.02 }}
                      />
                      <YAxis
                        yAxisId='left'
                        axisLine={false}
                        tickLine={false}
                        domain={[0, 200]}
                        ticks={Y_AXIS_REFERENCE_TICKS}
                        tickFormatter={(tick: number) => {
                          if (tick <= 100) return tick.toString();
                          const range = Math.max(niceDataMax - 100, 1);
                          const original = 100 + ((tick - 100) / 100) * range;
                          return Math.round(original).toString();
                        }}
                      />
                      <Tooltip content={<HealthChartTooltip />} />
                      {showChartSeries.heart && (
                        <Line
                          yAxisId='left'
                          type='monotone'
                          dataKey='heart'
                          stroke={HEALTH_CHART_SERIES.heart.color}
                          strokeWidth={CHART_LINE_STROKE_WIDTH}
                          connectNulls
                        />
                      )}
                      {showChartSeries.spo2 && (
                        <Line
                          yAxisId='left'
                          type='monotone'
                          dataKey='spo2'
                          stroke={HEALTH_CHART_SERIES.spo2.color}
                          strokeWidth={CHART_LINE_STROKE_WIDTH}
                          connectNulls
                        />
                      )}
                      {showChartSeries.temp && (
                        <Line
                          yAxisId='left'
                          type='monotone'
                          dataKey='temp'
                          stroke={HEALTH_CHART_SERIES.temp.color}
                          strokeWidth={CHART_LINE_STROKE_WIDTH}
                          connectNulls
                        />
                      )}
                      {showChartSeries.bp && (
                        <Line
                          yAxisId='left'
                          type='monotone'
                          dataKey='bp'
                          stroke={HEALTH_CHART_SERIES.bp.color}
                          strokeWidth={CHART_LINE_STROKE_WIDTH}
                          connectNulls
                        />
                      )}
                      {showChartSeries.steps && (
                        <Line
                          yAxisId='left'
                          type='monotone'
                          dataKey='steps'
                          stroke={HEALTH_CHART_SERIES.steps.color}
                          strokeWidth={CHART_LINE_STROKE_WIDTH}
                          connectNulls
                        />
                      )}
                      {/* Ẩn UI: Phát hiện không chuyển động
                      {showChartSeries.idle && (
                        <Line
                          yAxisId='left'
                          type='monotone'
                          dataKey='idle'
                          stroke={HEALTH_CHART_SERIES.idle.color}
                          strokeWidth={CHART_LINE_STROKE_WIDTH}
                          connectNulls
                        />
                      )}
                      */}
                      {/* Ẩn UI: Cảnh báo ngã
                      {showChartSeries.fall && (
                        <Line
                          yAxisId='left'
                          type='monotone'
                          dataKey='fall'
                          stroke={HEALTH_CHART_SERIES.fall.color}
                          strokeWidth={CHART_LINE_STROKE_WIDTH}
                          connectNulls
                        />
                      )}
                      */}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{ flexShrink: 0, width: CHART_LEGEND_COLUMN_WIDTH_PX, display: 'flex', alignItems: 'center', pb: 2 }}
            >
              <HealthChartLegend items={healthChartLegendItems} zoneHeightPx={chartLayout.legendZoneHeightPx} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default memo(PersonHealth);
